/**
 * Generated by orval v6.23.0 🍺
 * Do not edit manually.
 * APIs for RCI FE
 * This document describes the APIs provided by NeoAP BE for RCI FE, designed to be integrated into NeoAP FE using `iframe`.\
These APIs utilize cookie-based authentication. All API requests must include the cookie `_ap_session`.

 * OpenAPI spec version: 1
 */
import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult,
} from '@tanstack/react-query';
import type { BadRequestResponse } from '../model/badRequestResponse';
import type { ConflictResponse } from '../model/conflictResponse';
import type { ContentTooLargeResponse } from '../model/contentTooLargeResponse';
import type { ExportCustomTransactionItemOptionsBody } from '../model/exportCustomTransactionItemOptionsBody';
import type { ForbiddenResponse } from '../model/forbiddenResponse';
import type { GetCustomTransactionItemOptionsImportSampleFileParams } from '../model/getCustomTransactionItemOptionsImportSampleFileParams';
import type { ImportCustomTransactionItemOptions202 } from '../model/importCustomTransactionItemOptions202';
import type { ImportCustomTransactionItemOptionsBody } from '../model/importCustomTransactionItemOptionsBody';
import type { NotFoundResponse } from '../model/notFoundResponse';
import type { UnauthorizedResponse } from '../model/unauthorizedResponse';
import type { UnprocessableEntityResponse } from '../model/unprocessableEntityResponse';
import { useCustomClient } from '../../mutator/custom-instance';
import type { ErrorType, BodyType } from '../../mutator/custom-instance';
import { customFomDataFn } from '../../api/formData/custom-function';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P
) => any
  ? P
  : never;

/**
 * @summary Start import of custom transaction item options from CSV file
 */
export const useImportCustomTransactionItemOptionsHook = () => {
  const importCustomTransactionItemOptions =
    useCustomClient<ImportCustomTransactionItemOptions202>();

  return (
    id: string,
    importCustomTransactionItemOptionsBody: BodyType<ImportCustomTransactionItemOptionsBody>,
    options?: SecondParameter<ReturnType<typeof useCustomClient>>
  ) => {
    const formData = customFomDataFn(importCustomTransactionItemOptionsBody);
    return importCustomTransactionItemOptions(
      {
        url: `/api/js/custom_transaction_items/${id}/options/import`,
        method: 'POST',
        headers: { 'Content-Type': 'multipart/form-data' },
        data: formData,
      },
      options
    );
  };
};

export const useImportCustomTransactionItemOptionsMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | ConflictResponse
    | ContentTooLargeResponse
    | UnprocessableEntityResponse
  >,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<ReturnType<typeof useImportCustomTransactionItemOptionsHook>>
    >,
    TError,
    { id: string; data: BodyType<ImportCustomTransactionItemOptionsBody> },
    TContext
  >;
  request?: SecondParameter<ReturnType<typeof useCustomClient>>;
}): UseMutationOptions<
  Awaited<
    ReturnType<ReturnType<typeof useImportCustomTransactionItemOptionsHook>>
  >,
  TError,
  { id: string; data: BodyType<ImportCustomTransactionItemOptionsBody> },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const importCustomTransactionItemOptions =
    useImportCustomTransactionItemOptionsHook();

  const mutationFn: MutationFunction<
    Awaited<
      ReturnType<ReturnType<typeof useImportCustomTransactionItemOptionsHook>>
    >,
    { id: string; data: BodyType<ImportCustomTransactionItemOptionsBody> }
  > = (props) => {
    const { id, data } = props ?? {};

    return importCustomTransactionItemOptions(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ImportCustomTransactionItemOptionsMutationResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof useImportCustomTransactionItemOptionsHook>>
  >
>;
export type ImportCustomTransactionItemOptionsMutationBody =
  BodyType<ImportCustomTransactionItemOptionsBody>;
export type ImportCustomTransactionItemOptionsMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | ConflictResponse
  | ContentTooLargeResponse
  | UnprocessableEntityResponse
>;

/**
 * @summary Start import of custom transaction item options from CSV file
 */
export const useImportCustomTransactionItemOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | ConflictResponse
    | ContentTooLargeResponse
    | UnprocessableEntityResponse
  >,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<ReturnType<typeof useImportCustomTransactionItemOptionsHook>>
    >,
    TError,
    { id: string; data: BodyType<ImportCustomTransactionItemOptionsBody> },
    TContext
  >;
  request?: SecondParameter<ReturnType<typeof useCustomClient>>;
}) => {
  const mutationOptions =
    useImportCustomTransactionItemOptionsMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * @summary Get a sample CSV file for importing custom transaction item options
 */
export const useGetCustomTransactionItemOptionsImportSampleFileHook = () => {
  const getCustomTransactionItemOptionsImportSampleFile =
    useCustomClient<Blob>();

  return (
    params: GetCustomTransactionItemOptionsImportSampleFileParams,
    options?: SecondParameter<ReturnType<typeof useCustomClient>>,
    signal?: AbortSignal
  ) => {
    return getCustomTransactionItemOptionsImportSampleFile(
      {
        url: `/api/js/custom_transaction_items/options/import/sample_file`,
        method: 'GET',
        params,
        responseType: 'blob',
        signal,
      },
      options
    );
  };
};

export const getGetCustomTransactionItemOptionsImportSampleFileQueryKey = (
  params: GetCustomTransactionItemOptionsImportSampleFileParams
) => {
  return [
    `/api/js/custom_transaction_items/options/import/sample_file`,
    ...(params ? [params] : []),
  ] as const;
};

export const useGetCustomTransactionItemOptionsImportSampleFileQueryOptions = <
  TData = Awaited<
    ReturnType<
      ReturnType<typeof useGetCustomTransactionItemOptionsImportSampleFileHook>
    >
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >
>(
  params: GetCustomTransactionItemOptionsImportSampleFileParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<
              typeof useGetCustomTransactionItemOptionsImportSampleFileHook
            >
          >
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetCustomTransactionItemOptionsImportSampleFileQueryKey(params);

  const getCustomTransactionItemOptionsImportSampleFile =
    useGetCustomTransactionItemOptionsImportSampleFileHook();

  const queryFn: QueryFunction<
    Awaited<
      ReturnType<
        ReturnType<
          typeof useGetCustomTransactionItemOptionsImportSampleFileHook
        >
      >
    >
  > = ({ signal }) =>
    getCustomTransactionItemOptionsImportSampleFile(
      params,
      requestOptions,
      signal
    );

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<
      ReturnType<
        ReturnType<
          typeof useGetCustomTransactionItemOptionsImportSampleFileHook
        >
      >
    >,
    TError,
    TData
  > & { queryKey: QueryKey };
};

export type GetCustomTransactionItemOptionsImportSampleFileQueryResult =
  NonNullable<
    Awaited<
      ReturnType<
        ReturnType<
          typeof useGetCustomTransactionItemOptionsImportSampleFileHook
        >
      >
    >
  >;
export type GetCustomTransactionItemOptionsImportSampleFileQueryError =
  ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >;

/**
 * @summary Get a sample CSV file for importing custom transaction item options
 */
export const useGetCustomTransactionItemOptionsImportSampleFile = <
  TData = Awaited<
    ReturnType<
      ReturnType<typeof useGetCustomTransactionItemOptionsImportSampleFileHook>
    >
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >
>(
  params: GetCustomTransactionItemOptionsImportSampleFileParams,
  options?: {
    query?: Partial<
      UseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<
              typeof useGetCustomTransactionItemOptionsImportSampleFileHook
            >
          >
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    useGetCustomTransactionItemOptionsImportSampleFileQueryOptions(
      params,
      options
    );

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

export const useGetCustomTransactionItemOptionsImportSampleFileSuspenseQueryOptions =
  <
    TData = Awaited<
      ReturnType<
        ReturnType<
          typeof useGetCustomTransactionItemOptionsImportSampleFileHook
        >
      >
    >,
    TError = ErrorType<
      | BadRequestResponse
      | UnauthorizedResponse
      | ForbiddenResponse
      | NotFoundResponse
      | UnprocessableEntityResponse
    >
  >(
    params: GetCustomTransactionItemOptionsImportSampleFileParams,
    options?: {
      query?: Partial<
        UseSuspenseQueryOptions<
          Awaited<
            ReturnType<
              ReturnType<
                typeof useGetCustomTransactionItemOptionsImportSampleFileHook
              >
            >
          >,
          TError,
          TData
        >
      >;
      request?: SecondParameter<ReturnType<typeof useCustomClient>>;
    }
  ) => {
    const { query: queryOptions, request: requestOptions } = options ?? {};

    const queryKey =
      queryOptions?.queryKey ??
      getGetCustomTransactionItemOptionsImportSampleFileQueryKey(params);

    const getCustomTransactionItemOptionsImportSampleFile =
      useGetCustomTransactionItemOptionsImportSampleFileHook();

    const queryFn: QueryFunction<
      Awaited<
        ReturnType<
          ReturnType<
            typeof useGetCustomTransactionItemOptionsImportSampleFileHook
          >
        >
      >
    > = ({ signal }) =>
      getCustomTransactionItemOptionsImportSampleFile(
        params,
        requestOptions,
        signal
      );

    return { queryKey, queryFn, ...queryOptions } as UseSuspenseQueryOptions<
      Awaited<
        ReturnType<
          ReturnType<
            typeof useGetCustomTransactionItemOptionsImportSampleFileHook
          >
        >
      >,
      TError,
      TData
    > & { queryKey: QueryKey };
  };

export type GetCustomTransactionItemOptionsImportSampleFileSuspenseQueryResult =
  NonNullable<
    Awaited<
      ReturnType<
        ReturnType<
          typeof useGetCustomTransactionItemOptionsImportSampleFileHook
        >
      >
    >
  >;
export type GetCustomTransactionItemOptionsImportSampleFileSuspenseQueryError =
  ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >;

/**
 * @summary Get a sample CSV file for importing custom transaction item options
 */
export const useGetCustomTransactionItemOptionsImportSampleFileSuspense = <
  TData = Awaited<
    ReturnType<
      ReturnType<typeof useGetCustomTransactionItemOptionsImportSampleFileHook>
    >
  >,
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | UnprocessableEntityResponse
  >
>(
  params: GetCustomTransactionItemOptionsImportSampleFileParams,
  options?: {
    query?: Partial<
      UseSuspenseQueryOptions<
        Awaited<
          ReturnType<
            ReturnType<
              typeof useGetCustomTransactionItemOptionsImportSampleFileHook
            >
          >
        >,
        TError,
        TData
      >
    >;
    request?: SecondParameter<ReturnType<typeof useCustomClient>>;
  }
): UseSuspenseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions =
    useGetCustomTransactionItemOptionsImportSampleFileSuspenseQueryOptions(
      params,
      options
    );

  const query = useSuspenseQuery(queryOptions) as UseSuspenseQueryResult<
    TData,
    TError
  > & { queryKey: QueryKey };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * @summary Start export of custom transaction item options to CSV
 */
export const useExportCustomTransactionItemOptionsHook = () => {
  const exportCustomTransactionItemOptions = useCustomClient<unknown>();

  return (
    id: string,
    exportCustomTransactionItemOptionsBody: BodyType<ExportCustomTransactionItemOptionsBody>,
    options?: SecondParameter<ReturnType<typeof useCustomClient>>
  ) => {
    return exportCustomTransactionItemOptions(
      {
        url: `/api/js/custom_transaction_items/${id}/options/export`,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        data: exportCustomTransactionItemOptionsBody,
      },
      options
    );
  };
};

export const useExportCustomTransactionItemOptionsMutationOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | ConflictResponse
    | UnprocessableEntityResponse
  >,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<ReturnType<typeof useExportCustomTransactionItemOptionsHook>>
    >,
    TError,
    { id: string; data: BodyType<ExportCustomTransactionItemOptionsBody> },
    TContext
  >;
  request?: SecondParameter<ReturnType<typeof useCustomClient>>;
}): UseMutationOptions<
  Awaited<
    ReturnType<ReturnType<typeof useExportCustomTransactionItemOptionsHook>>
  >,
  TError,
  { id: string; data: BodyType<ExportCustomTransactionItemOptionsBody> },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const exportCustomTransactionItemOptions =
    useExportCustomTransactionItemOptionsHook();

  const mutationFn: MutationFunction<
    Awaited<
      ReturnType<ReturnType<typeof useExportCustomTransactionItemOptionsHook>>
    >,
    { id: string; data: BodyType<ExportCustomTransactionItemOptionsBody> }
  > = (props) => {
    const { id, data } = props ?? {};

    return exportCustomTransactionItemOptions(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type ExportCustomTransactionItemOptionsMutationResult = NonNullable<
  Awaited<
    ReturnType<ReturnType<typeof useExportCustomTransactionItemOptionsHook>>
  >
>;
export type ExportCustomTransactionItemOptionsMutationBody =
  BodyType<ExportCustomTransactionItemOptionsBody>;
export type ExportCustomTransactionItemOptionsMutationError = ErrorType<
  | BadRequestResponse
  | UnauthorizedResponse
  | ForbiddenResponse
  | NotFoundResponse
  | ConflictResponse
  | UnprocessableEntityResponse
>;

/**
 * @summary Start export of custom transaction item options to CSV
 */
export const useExportCustomTransactionItemOptions = <
  TError = ErrorType<
    | BadRequestResponse
    | UnauthorizedResponse
    | ForbiddenResponse
    | NotFoundResponse
    | ConflictResponse
    | UnprocessableEntityResponse
  >,
  TContext = unknown
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<
      ReturnType<ReturnType<typeof useExportCustomTransactionItemOptionsHook>>
    >,
    TError,
    { id: string; data: BodyType<ExportCustomTransactionItemOptionsBody> },
    TContext
  >;
  request?: SecondParameter<ReturnType<typeof useCustomClient>>;
}) => {
  const mutationOptions =
    useExportCustomTransactionItemOptionsMutationOptions(options);

  return useMutation(mutationOptions);
};
